/**
 * CNAE Data Service
 *
 * Queries the cnae_codes collection in the Appwrite brain database — the
 * source of truth for the ISR workspace's CNAE dropdown. Codes are stored in
 * the DOTTED format ("49.41") byte-identical to n8n's
 * lvl1_company.cnae_code, so a selection maps 1:1 to the wire value with no
 * conversion on either side.
 *
 * The whole catalogue is 736 rows — one paginated fetch (limit 1000), cached
 * in-module. GeoSelect filters client-side, so there is no server-side search.
 */

import { Client, Databases, Query } from 'appwrite';
import { createAsyncRequestCache } from '../utils/asyncRequestCache';

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID || '693c1ae3003d56af4154';
const COLLECTION_CNAE = 'cnae_codes';

const client = new Client()
  .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT || '')
  .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID || '');

const databases = new Databases(client);

export interface CnaeCode {
  /** Dotted code, e.g. "49.41" — the exact wire value. */
  code: string;
  name_en: string;
  name_es: string;
  /** Code + ES/EN names + keywords — prebuilt for fuzzy search. */
  search_text: string;
}

/**
 * Locale-aware activity label for a CNAE row. English is the platform default
 * today; Spanish resolves for any `es`-family tag. When the per-user language
 * switcher lands, pass the user's locale here — no other call-site changes.
 */
export const cnaeLabelFor = (row: CnaeCode, locale: string): string => {
  const isSpanish = locale?.toLowerCase().startsWith('es');
  return (isSpanish ? row.name_es : row.name_en) || row.name_es || row.name_en || row.code;
};

/**
 * In-flight Promise cache for the CNAE catalogue.
 *
 * The catalogue is static, so concurrent `listCnaeCodes()` calls (several
 * components mounting at once) must share ONE network request: the first
 * caller stores its Promise here and every later caller awaits it instead
 * of re-fetching. Resolved values are cached for the session; failures are
 * NOT cached, so a retry re-fires (matches the throw-on-transient-errors
 * pattern — see memory `catch-return-fallback-caches-false`).
 */
const cnaeCache = createAsyncRequestCache<CnaeCode[]>({
  // Static reference data — never expires in-session.
  ttlMs: Number.MAX_SAFE_INTEGER,
});

export const cnaeService = {
  listCnaeCodes(): Promise<CnaeCode[]> {
    return cnaeCache.get('cnae', async () => {
      // This Appwrite build caps page size at 25 regardless of the requested
      // limit — paginate via cursor until all 736 codes are loaded, otherwise
      // the dropdown silently shows only the first 25.
      const docs: Record<string, any>[] = [];
      let cursor: string | undefined;
      do {
        const queries = [Query.orderAsc('code'), Query.orderAsc('$id'), Query.limit(25)];
        if (cursor) queries.push(Query.cursorAfter(cursor));
        const result = await databases.listDocuments(DATABASE_ID, COLLECTION_CNAE, queries);
        docs.push(...(result.documents as Record<string, any>[]));
        cursor = result.documents.length > 0
          ? result.documents[result.documents.length - 1].$id
          : undefined;
        if (result.documents.length < 25) break;
      } while (cursor);

      return docs.map((doc) => ({
        code: doc.code,
        name_en: doc.name_en || '',
        name_es: doc.name_es || '',
        search_text: doc.search_text || '',
      }));
    });
  },

  clearCache(): void {
    cnaeCache.clear();
  },
};
